import { useCallback, useRef, useState } from 'react';
import { logger } from '@/utils/logger';

// Effect parameter interfaces
export interface PitchShiftParams {
  semitones: number; // -12 to +12
}

export interface EQParams {
  lowGain: number;    // -12 to +12 dB
  midGain: number;    // -12 to +12 dB
  highGain: number;   // -12 to +12 dB
  lowFreq: number;    // Hz (default 320)
  highFreq: number;   // Hz (default 3200)
}

export interface FilterParams {
  type: BiquadFilterType;
  frequency: number;  // 20 to 20000 Hz
  Q: number;          // 0.1 to 20
  gain: number;       // -40 to 40 dB (for peaking/shelving)
}

export interface ReverbParams {
  wet: number;        // 0 to 1
  decay: number;      // 0.1 to 10 seconds
  preDelay: number;   // 0 to 100 ms
}

export interface DistortionParams {
  amount: number;     // 0 to 100
  type: 'soft' | 'hard' | 'fuzz' | 'bitcrush';
}

export interface DelayParams {
  time: number;       // 0 to 2000 ms
  feedback: number;   // 0 to 0.95
  wet: number;        // 0 to 1
}

export interface CustomDSPParams {
  code: string;       // User's DSP code
  enabled: boolean;
}

export interface AudioEffectsState {
  pitchShift: PitchShiftParams & { enabled: boolean };
  eq: EQParams & { enabled: boolean };
  filter: FilterParams & { enabled: boolean };
  reverb: ReverbParams & { enabled: boolean };
  distortion: DistortionParams & { enabled: boolean };
  delay: DelayParams & { enabled: boolean };
  customDSP: CustomDSPParams;
  masterGain: number; // 0 to 2
}

const DEFAULT_STATE: AudioEffectsState = {
  pitchShift: { enabled: false, semitones: 0 },
  eq: { enabled: false, lowGain: 0, midGain: 0, highGain: 0, lowFreq: 320, highFreq: 3200 },
  filter: { enabled: false, type: 'lowpass', frequency: 1000, Q: 1, gain: 0 },
  reverb: { enabled: false, wet: 0.3, decay: 2, preDelay: 20 },
  distortion: { enabled: false, amount: 50, type: 'soft' },
  delay: { enabled: false, time: 300, feedback: 0.4, wet: 0.3 },
  customDSP: { enabled: false, code: `// Custom DSP Function
// Input: samples (Float32Array) - audio samples
// Output: processed samples (modify in place)
// sampleRate: number - audio sample rate

function process(samples, sampleRate) {
  // Example: Simple gain
  for (let i = 0; i < samples.length; i++) {
    samples[i] *= 1.0; // Modify this
  }
}` },
  masterGain: 1,
};

// Generate impulse response for reverb
function createImpulseResponse(
  audioContext: AudioContext,
  decay: number,
  preDelay: number
): AudioBuffer {
  const sampleRate = audioContext.sampleRate;
  const length = sampleRate * decay;
  const preDelaySamples = Math.floor((preDelay / 1000) * sampleRate);
  const impulse = audioContext.createBuffer(2, length + preDelaySamples, sampleRate);

  for (let channel = 0; channel < 2; channel++) {
    const channelData = impulse.getChannelData(channel);
    for (let i = preDelaySamples; i < length + preDelaySamples; i++) {
      const t = (i - preDelaySamples) / length;
      // Exponential decay with some randomness for natural sound
      channelData[i] = (Math.random() * 2 - 1) * Math.exp(-3 * t);
    }
  }

  return impulse;
}

// Generate distortion curve
function createDistortionCurve(amount: number, type: string): Float32Array | null {
  const samples = 44100;
  const curve = new Float32Array(samples);
  const k = amount;

  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;

    switch (type) {
      case 'soft':
        // Soft clipping using tanh
        curve[i] = Math.tanh(k * x / 50);
        break;
      case 'hard':
        // Hard clipping
        curve[i] = Math.max(-1, Math.min(1, k * x / 25));
        break;
      case 'fuzz':
        // Asymmetric fuzz
        if (x >= 0) {
          curve[i] = Math.tanh(k * x / 30);
        } else {
          curve[i] = Math.tanh(k * x / 60);
        }
        break;
      case 'bitcrush':
        // Bit reduction effect
        const bits = Math.max(1, 16 - Math.floor(k / 7));
        const step = 2 / Math.pow(2, bits);
        curve[i] = Math.round(x / step) * step;
        break;
      default:
        curve[i] = x;
    }
  }

  return curve as Float32Array;
}

export function useAudioEffects() {
  const [state, setState] = useState<AudioEffectsState>(DEFAULT_STATE);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Audio nodes refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const eqLowRef = useRef<BiquadFilterNode | null>(null);
  const eqMidRef = useRef<BiquadFilterNode | null>(null);
  const eqHighRef = useRef<BiquadFilterNode | null>(null);
  const filterNodeRef = useRef<BiquadFilterNode | null>(null);
  const convolverRef = useRef<ConvolverNode | null>(null);
  const reverbGainRef = useRef<GainNode | null>(null);
  const dryGainRef = useRef<GainNode | null>(null);
  const waveShaperRef = useRef<WaveShaperNode | null>(null);
  const delayNodeRef = useRef<DelayNode | null>(null);
  const delayFeedbackRef = useRef<GainNode | null>(null);
  const delayWetRef = useRef<GainNode | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const originalBufferRef = useRef<AudioBuffer | null>(null);

  // Update effect parameter
  const updateEffect = useCallback(<K extends keyof AudioEffectsState>(
    effect: K,
    params: Partial<AudioEffectsState[K]>
  ) => {
    setState(prev => {
      const currentValue = prev[effect];
      if (typeof currentValue === 'object' && currentValue !== null) {
        return {
          ...prev,
          [effect]: { ...currentValue, ...params }
        };
      }
      // For primitive values like masterGain
      return {
        ...prev,
        [effect]: params
      };
    });
  }, []);

  // Toggle effect on/off
  const toggleEffect = useCallback((effect: keyof Omit<AudioEffectsState, 'masterGain'>) => {
    setState(prev => ({
      ...prev,
      [effect]: { ...prev[effect], enabled: !prev[effect].enabled }
    }));
  }, []);

  // Reset all effects to default
  const resetEffects = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  // Process audio with all enabled effects
  const processAudio = useCallback(async (audioBlob: Blob): Promise<Blob> => {
    setIsProcessing(true);
    setError(null);

    try {
      // Create audio context
      const audioContext = new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Decode audio
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      originalBufferRef.current = audioBuffer;

      // Create offline context for processing
      const offlineContext = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        audioBuffer.length,
        audioBuffer.sampleRate
      );

      // Create source
      const source = offlineContext.createBufferSource();
      source.buffer = audioBuffer;

      // Build effect chain
      let currentNode: AudioNode = source;

      // Pitch shift (using playback rate - simple but changes tempo)
      if (state.pitchShift.enabled && state.pitchShift.semitones !== 0) {
        source.playbackRate.value = Math.pow(2, state.pitchShift.semitones / 12);
      }

      // EQ (3-band)
      if (state.eq.enabled) {
        const lowShelf = offlineContext.createBiquadFilter();
        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = state.eq.lowFreq;
        lowShelf.gain.value = state.eq.lowGain;

        const peaking = offlineContext.createBiquadFilter();
        peaking.type = 'peaking';
        peaking.frequency.value = Math.sqrt(state.eq.lowFreq * state.eq.highFreq);
        peaking.Q.value = 1;
        peaking.gain.value = state.eq.midGain;

        const highShelf = offlineContext.createBiquadFilter();
        highShelf.type = 'highshelf';
        highShelf.frequency.value = state.eq.highFreq;
        highShelf.gain.value = state.eq.highGain;

        currentNode.connect(lowShelf);
        lowShelf.connect(peaking);
        peaking.connect(highShelf);
        currentNode = highShelf;
      }

      // Filter
      if (state.filter.enabled) {
        const filter = offlineContext.createBiquadFilter();
        filter.type = state.filter.type;
        filter.frequency.value = state.filter.frequency;
        filter.Q.value = state.filter.Q;
        if (['lowshelf', 'highshelf', 'peaking'].includes(state.filter.type)) {
          filter.gain.value = state.filter.gain;
        }
        currentNode.connect(filter);
        currentNode = filter;
      }

      // Distortion
      if (state.distortion.enabled) {
        const waveShaper = offlineContext.createWaveShaper();
        const curve = createDistortionCurve(state.distortion.amount, state.distortion.type);
        if (curve) {
          (waveShaper as { curve: Float32Array | null }).curve = curve;
        }
        waveShaper.oversample = '4x';
        currentNode.connect(waveShaper);
        currentNode = waveShaper;
      }

      // Delay/Echo
      if (state.delay.enabled) {
        const dryGain = offlineContext.createGain();
        dryGain.gain.value = 1 - state.delay.wet;

        const wetGain = offlineContext.createGain();
        wetGain.gain.value = state.delay.wet;

        const delay = offlineContext.createDelay(5);
        delay.delayTime.value = state.delay.time / 1000;

        const feedback = offlineContext.createGain();
        feedback.gain.value = state.delay.feedback;

        const merger = offlineContext.createGain();

        currentNode.connect(dryGain);
        currentNode.connect(delay);
        delay.connect(feedback);
        feedback.connect(delay);
        delay.connect(wetGain);
        dryGain.connect(merger);
        wetGain.connect(merger);
        currentNode = merger;
      }

      // Reverb
      if (state.reverb.enabled) {
        const dryGain = offlineContext.createGain();
        dryGain.gain.value = 1 - state.reverb.wet;

        const wetGain = offlineContext.createGain();
        wetGain.gain.value = state.reverb.wet;

        const convolver = offlineContext.createConvolver();
        convolver.buffer = createImpulseResponse(
          offlineContext as unknown as AudioContext,
          state.reverb.decay,
          state.reverb.preDelay
        );

        const merger = offlineContext.createGain();

        currentNode.connect(dryGain);
        currentNode.connect(convolver);
        convolver.connect(wetGain);
        dryGain.connect(merger);
        wetGain.connect(merger);
        currentNode = merger;
      }

      // Master gain
      const masterGain = offlineContext.createGain();
      masterGain.gain.value = state.masterGain;
      currentNode.connect(masterGain);
      masterGain.connect(offlineContext.destination);

      // Start processing
      source.start(0);
      const renderedBuffer = await offlineContext.startRendering();

      // Convert to blob
      const wavBlob = await audioBufferToWav(renderedBuffer);

      await audioContext.close();
      setIsProcessing(false);

      return wavBlob;
    } catch (err) {
      logger.error('Audio processing error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process audio');
      setIsProcessing(false);
      throw err;
    }
  }, [state]);

  // Play audio with real-time effects (for preview)
  const playWithEffects = useCallback(async (audioBlob: Blob) => {
    try {
      // Stop any existing playback
      if (sourceNodeRef.current) {
        sourceNodeRef.current.stop();
        sourceNodeRef.current = null;
      }

      const audioContext = new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      audioContextRef.current = audioContext;

      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      sourceNodeRef.current = source;

      // Build live effect chain (similar to processAudio but with live nodes)
      let currentNode: AudioNode = source;

      // Pitch shift
      if (state.pitchShift.enabled && state.pitchShift.semitones !== 0) {
        source.playbackRate.value = Math.pow(2, state.pitchShift.semitones / 12);
      }

      // EQ
      if (state.eq.enabled) {
        const lowShelf = audioContext.createBiquadFilter();
        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = state.eq.lowFreq;
        lowShelf.gain.value = state.eq.lowGain;
        eqLowRef.current = lowShelf;

        const peaking = audioContext.createBiquadFilter();
        peaking.type = 'peaking';
        peaking.frequency.value = Math.sqrt(state.eq.lowFreq * state.eq.highFreq);
        peaking.Q.value = 1;
        peaking.gain.value = state.eq.midGain;
        eqMidRef.current = peaking;

        const highShelf = audioContext.createBiquadFilter();
        highShelf.type = 'highshelf';
        highShelf.frequency.value = state.eq.highFreq;
        highShelf.gain.value = state.eq.highGain;
        eqHighRef.current = highShelf;

        currentNode.connect(lowShelf);
        lowShelf.connect(peaking);
        peaking.connect(highShelf);
        currentNode = highShelf;
      }

      // Filter
      if (state.filter.enabled) {
        const filter = audioContext.createBiquadFilter();
        filter.type = state.filter.type;
        filter.frequency.value = state.filter.frequency;
        filter.Q.value = state.filter.Q;
        if (['lowshelf', 'highshelf', 'peaking'].includes(state.filter.type)) {
          filter.gain.value = state.filter.gain;
        }
        filterNodeRef.current = filter;
        currentNode.connect(filter);
        currentNode = filter;
      }

      // Distortion
      if (state.distortion.enabled) {
        const waveShaper = audioContext.createWaveShaper();
        const curve = createDistortionCurve(state.distortion.amount, state.distortion.type);
        if (curve) {
          (waveShaper as { curve: Float32Array | null }).curve = curve;
        }
        waveShaper.oversample = '4x';
        waveShaperRef.current = waveShaper;
        currentNode.connect(waveShaper);
        currentNode = waveShaper;
      }

      // Delay
      if (state.delay.enabled) {
        const dryGain = audioContext.createGain();
        dryGain.gain.value = 1 - state.delay.wet;
        dryGainRef.current = dryGain;

        const wetGain = audioContext.createGain();
        wetGain.gain.value = state.delay.wet;
        delayWetRef.current = wetGain;

        const delay = audioContext.createDelay(5);
        delay.delayTime.value = state.delay.time / 1000;
        delayNodeRef.current = delay;

        const feedback = audioContext.createGain();
        feedback.gain.value = state.delay.feedback;
        delayFeedbackRef.current = feedback;

        const merger = audioContext.createGain();

        currentNode.connect(dryGain);
        currentNode.connect(delay);
        delay.connect(feedback);
        feedback.connect(delay);
        delay.connect(wetGain);
        dryGain.connect(merger);
        wetGain.connect(merger);
        currentNode = merger;
      }

      // Reverb
      if (state.reverb.enabled) {
        const dryGain = audioContext.createGain();
        dryGain.gain.value = 1 - state.reverb.wet;

        const wetGain = audioContext.createGain();
        wetGain.gain.value = state.reverb.wet;
        reverbGainRef.current = wetGain;

        const convolver = audioContext.createConvolver();
        convolver.buffer = createImpulseResponse(audioContext, state.reverb.decay, state.reverb.preDelay);
        convolverRef.current = convolver;

        const merger = audioContext.createGain();

        currentNode.connect(dryGain);
        currentNode.connect(convolver);
        convolver.connect(wetGain);
        dryGain.connect(merger);
        wetGain.connect(merger);
        currentNode = merger;
      }

      // Master gain
      const masterGain = audioContext.createGain();
      masterGain.gain.value = state.masterGain;
      masterGainRef.current = masterGain;
      currentNode.connect(masterGain);
      masterGain.connect(audioContext.destination);

      source.onended = () => {
        audioContext.close();
        sourceNodeRef.current = null;
      };

      source.start(0);
    } catch (err) {
      logger.error('Playback error:', err);
      setError(err instanceof Error ? err.message : 'Failed to play audio');
    }
  }, [state]);

  // Stop playback
  const stopPlayback = useCallback(() => {
    if (sourceNodeRef.current) {
      sourceNodeRef.current.stop();
      sourceNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  return {
    state,
    isProcessing,
    error,
    updateEffect,
    toggleEffect,
    resetEffects,
    processAudio,
    playWithEffects,
    stopPlayback,
  };
}

// Convert AudioBuffer to WAV blob
async function audioBufferToWav(buffer: AudioBuffer): Promise<Blob> {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = buffer.length * blockAlign;
  const headerSize = 44;
  const fileSize = headerSize + dataSize;

  const arrayBuffer = new ArrayBuffer(fileSize);
  const view = new DataView(arrayBuffer);

  // Write WAV header
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, fileSize - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  // Interleave channels and write samples
  const channels: Float32Array[] = [];
  for (let i = 0; i < numChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }

  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, int16, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

export default useAudioEffects;
